// TODO: refactor this file in the future
/* eslint-disable react-compiler/react-compiler */
import { Collapse, formGroupClasses, Grid, Stack, styled } from '@mui/material';
import { FormButton, FormControlGroup, FormNumberField } from '@oneview/controls';
import { FormValues, getInitialFormValues } from '@/utils/formUtils';
import { generateNamesObject } from '@/utils/stringUtils';
import { Form, Formik } from 'formik';
import { isEqual } from 'lodash';
import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import { AnyObject, mixed, number, object } from 'yup';

export enum Type {
    Automatically,
    Manually,
}

export enum Method {
    SetAllTo,
    IncreaseBy,
    DecreaseBy,
    Equally,
    Proportionally,
}

export enum Unit {
    Share,
    Dollar,
    Percentage,
    Unit,
}

export enum RelativeTo {
    AccountBalance,
    CurrentPosition,
}

export const allocationFormClasses = generateNamesObject(['button'], 'AllocationForm');

const Container = styled(Grid)(({ theme }) => ({
    [`& .${formGroupClasses.root}`]: {
        margin: theme.spacing(0, 2, 1, 0),
    },
    [`& .${allocationFormClasses.button}`]: {
        boxShadow: 'none',
        textTransform: 'none',
        fontWeight: 'normal',
        height: 40,
        marginRight: theme.spacing(1),
        '&:hover': {
            boxShadow: 'none',
        },
    },
}));

const fieldNames = generateNamesObject('amount', 'type', 'method', 'unit', 'relativeTo');

const allocationSchema = object({
    [fieldNames.amount]: number()
        .default(0)
        .required()
        .transform((value) => (isNaN(value) ? 0 : value)),
    [fieldNames.type]: mixed<Type>().default(Type.Automatically).required(),
    [fieldNames.method]: mixed<Method>().default(Method.SetAllTo).required(),
    [fieldNames.unit]: mixed<Unit>().default(Unit.Percentage).required(),
    [fieldNames.relativeTo]: mixed<RelativeTo>().default(RelativeTo.AccountBalance).required(),
});

export const getInitialValues = (values?: AnyObject) => getInitialFormValues(allocationSchema, values);

export type AllocationFormValues = FormValues<typeof allocationSchema>;

type Props = {
    activeBlockIndex: number;
    totalBlocks: number;
    values: AllocationFormValues;
    preallocation?: boolean;
    onChange?: (values: AllocationFormValues) => void;
    onSubmit?: (values: AllocationFormValues) => void;
    submitRef?: RefObject<HTMLButtonElement>;
};

export const AllocationForm: React.FunctionComponent<Props> = ({
    onChange,
    submitRef,
    preallocation,
    activeBlockIndex,
    totalBlocks,
    values,
}) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialValues = useMemo(() => getInitialValues(values), [activeBlockIndex]);

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={allocationSchema}
            onSubmit={(values) => alert(JSON.stringify(values))}
        >
            {({ values: internalValues, setValues }) => {
                // this is react component, not callback function
                /* eslint-disable react-hooks/rules-of-hooks */

                const didMountRef = useRef(false);
                useEffect(() => {
                    if (didMountRef.current) {
                        onChange?.(internalValues);
                    } else {
                        didMountRef.current = true;
                    }
                }, [internalValues]);

                useEffect(() => {
                    const newValues = getInitialValues(values);

                    // values have been changed outside of the form
                    if (!isEqual(internalValues, newValues)) {
                        setValues(newValues);
                    }
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                }, [values]);

                const { amount, type, method, unit } = internalValues;

                /* eslint-enable react-hooks/rules-of-hooks */

                return (
                    <Form>
                        <Container container spacing={1}>
                            <Grid item xs={12}>
                                <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
                                    <FormControlGroup row label="Type" labelGutter name={fieldNames.type}>
                                        <FormButton
                                            size="small"
                                            name={fieldNames.type}
                                            value={Type.Automatically}
                                            className={allocationFormClasses.button}
                                        >
                                            Automatically
                                        </FormButton>

                                        <FormButton
                                            size="small"
                                            name={fieldNames.type}
                                            value={Type.Manually}
                                            className={allocationFormClasses.button}
                                        >
                                            Manually
                                        </FormButton>
                                    </FormControlGroup>

                                    <FormControlGroup
                                        row
                                        label="Method"
                                        labelGutter
                                        name={fieldNames.method}
                                        sx={{ display: type === Type.Automatically ? undefined : 'none' }}
                                    >
                                        <FormButton
                                            size="small"
                                            name={fieldNames.method}
                                            value={Method.SetAllTo}
                                            className={allocationFormClasses.button}
                                        >
                                            Set all to
                                        </FormButton>

                                        {totalBlocks > 1 && (
                                            <>
                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.method}
                                                    value={Method.IncreaseBy}
                                                    className={allocationFormClasses.button}
                                                >
                                                    Increase by
                                                </FormButton>

                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.method}
                                                    value={Method.DecreaseBy}
                                                    className={allocationFormClasses.button}
                                                >
                                                    Decrease by
                                                </FormButton>
                                            </>
                                        )}

                                        {!preallocation && (
                                            <>
                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.method}
                                                    value={Method.Equally}
                                                    className={allocationFormClasses.button}
                                                >
                                                    Equally
                                                </FormButton>

                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.method}
                                                    value={Method.Proportionally}
                                                    className={allocationFormClasses.button}
                                                >
                                                    Proportionally
                                                </FormButton>
                                            </>
                                        )}
                                    </FormControlGroup>
                                </Stack>

                                <Collapse in={type === Type.Automatically} unmountOnExit>
                                    <Stack direction="row" sx={{ flexWrap: 'wrap', mt: 3 }}>
                                        {method !== Method.Equally && method !== Method.Proportionally && (
                                            <FormControlGroup row label="Amount" labelGutter name={fieldNames.amount}>
                                                <FormNumberField
                                                    size="small"
                                                    name={fieldNames.amount}
                                                    value={amount}
                                                    style={{ width: 150 }}
                                                />
                                            </FormControlGroup>
                                        )}

                                        {method !== Method.Equally && method !== Method.Proportionally && (
                                            <FormControlGroup row label="Unit" labelGutter name={fieldNames.unit}>
                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.unit}
                                                    value={Unit.Percentage}
                                                    className={allocationFormClasses.button}
                                                >
                                                    %
                                                </FormButton>

                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.unit}
                                                    value={Unit.Dollar}
                                                    className={allocationFormClasses.button}
                                                >
                                                    $
                                                </FormButton>

                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.unit}
                                                    value={Unit.Share}
                                                    className={allocationFormClasses.button}
                                                >
                                                    Share
                                                </FormButton>
                                            </FormControlGroup>
                                        )}

                                        {method !== Method.Equally && unit === Unit.Percentage && (
                                            <FormControlGroup
                                                row
                                                label="Relative to"
                                                labelGutter
                                                name={fieldNames.relativeTo}
                                            >
                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.relativeTo}
                                                    value={RelativeTo.AccountBalance}
                                                    className={allocationFormClasses.button}
                                                >
                                                    Account balance
                                                </FormButton>

                                                <FormButton
                                                    size="small"
                                                    name={fieldNames.relativeTo}
                                                    value={RelativeTo.CurrentPosition}
                                                    className={allocationFormClasses.button}
                                                >
                                                    Current position
                                                </FormButton>
                                            </FormControlGroup>
                                        )}
                                    </Stack>
                                </Collapse>
                            </Grid>

                            <button type="submit" ref={submitRef} style={{ display: 'none' }} />
                        </Container>
                    </Form>
                );
            }}
        </Formik>
    );
};
